import type { MessageDescriptor } from 'react-intl';

import type { FormatMessage } from '@tmapy/intl';

import type { PriceBandResponse } from '../openapi/types/PriceBand';
import type { PriceListItemUnitsEnum } from '../openapi/types/PriceListItemUnitsEnum';
import { PriceListItemTypesEnum } from '../openapi/types/PriceListItemTypesEnum';

import {
  priceListItemUnitQuantityMessages,
  priceListMessages,
} from './components/priceListMessages';

/**
 * Typy, jejichž cena je daná pásmy. Nezadává se u nich jednotka ani cena za
 * jednotku a `unit_price_with_vat` chodí jako null.
 */
export const DATA_PRICE_LIST_ITEM_TYPES = [
  PriceListItemTypesEnum.DATA_VECTOR,
  PriceListItemTypesEnum.DATA_RASTER,
];

/**
 * Rozlišovacím kritériem je typ, ne přítomnost pásem: datová položka je datová
 * i ve chvíli, kdy jí v administraci ještě nikdo pásma nevyplnil.
 */
export const isDataPriceListItemType = (type: PriceListItemTypesEnum | undefined): boolean =>
  type === PriceListItemTypesEnum.DATA_VECTOR || type === PriceListItemTypesEnum.DATA_RASTER;

type PriceBandRange = {
  descriptor: MessageDescriptor;
  values: { from?: number; to?: number };
};

/**
 * Vybere tvar popisku rozsahu pásma. `from_ha` je exkluzivní, `to_ha` včetně,
 * `to_ha: null` znamená bez horního limitu — odtud „do 125 ha“, „125 – 12 500 ha“
 * a „nad 37 500 ha“. Pořadí podmínek drží dohromady i degenerovaný případ
 * jediného pásma od nuly bez limitu, které by jinak vyšlo jako „nad 0 ha“.
 */
export const getPriceBandRange = (band: PriceBandResponse): PriceBandRange => {
  const from = Number(band.from_ha);
  const to = band.to_ha === null ? null : Number(band.to_ha);

  if (to === null) {
    return from === 0
      ? { descriptor: priceListMessages.bandsRangeAny, values: {} }
      : { descriptor: priceListMessages.bandsRangeOver, values: { from } };
  }

  return from === 0
    ? { descriptor: priceListMessages.bandsRangeUpTo, values: { to } }
    : { descriptor: priceListMessages.bandsRangeBetween, values: { from, to } };
};

/** Nulové pásmo se zobrazuje jako „zdarma“, ne jako „0 Kč“. */
export const isPriceBandFree = (band: PriceBandResponse): boolean => Number(band.price) === 0;

/**
 * Do přehledu se z pásem vypisuje jen rozpětí cen, celá tabulka patří do detailu.
 * Vrací null, dokud položka pásma nemá — přehled pak nechá buňku prázdnou.
 */
export const getPriceBandsPriceRange = (
  bands: PriceBandResponse[] | null,
): { min: number; max: number } | null => {
  if (!bands?.length) return null;

  const prices = bands.map((band) => Number(band.price));
  return { min: Math.min(...prices), max: Math.max(...prices) };
};

/**
 * Počet se skloňovanou jednotkou. Bez jednotky (datové položky ji nemají) i pro
 * jednotku, kterou server přidá dřív než frontend, vypíše aspoň samotné číslo —
 * prázdné pole by v přehledu vypadalo jako chybějící data.
 */
export const formatQuantityWithUnit = (
  formatMessage: FormatMessage,
  quantity: number,
  unit: PriceListItemUnitsEnum | null | undefined,
): string => {
  const withUnit = unit
    ? formatMessage.lookup(priceListItemUnitQuantityMessages, unit, { quantity })
    : null;

  return (
    withUnit ?? formatMessage(priceListMessages.quantityFallback, { quantity, unit: unit ?? '' })
  );
};
